import * as React from 'react';
import { Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { useForm } from 'react-hook-form';
import FormInputText from "../../form/FormInputText";
import { WorkingSeason } from "./useWorkingSeason";
import randomUUID from "../../common/randomUUID";
import { Question } from "../../../domain/Season";

interface Props {
  readonly workingSeason: WorkingSeason
}

interface AddQuestionFormData {
  readonly number: string;
  readonly name: string;
  readonly points: number | '';
}

function calculateNextQuestionNumber(questions: Question[]): string {
  if (questions.length === 0) {
    return '';
  }

  const lastQuestion = questions[questions.length - 1];
  const lastNumber = lastQuestion.number;

  // Try to parse the pattern: prefix (letters) + numeric suffix
  const match = lastNumber.match(/^([A-Za-z]*)(\d+)$/);

  if (match) {
    const prefix = match[1]; // e.g., "A" or ""
    const numericPart = parseInt(match[2]); // e.g., 5
    return prefix + (numericPart + 1); // e.g., "A6"
  }

  // Doesn't match pattern, return empty
  return '';
}

export default function AddQuestionForm({ workingSeason }: Props) {
  const nextNumber = calculateNextQuestionNumber(workingSeason.seasonView.questions);

  const { control, handleSubmit, reset, setFocus, setValue } = useForm<AddQuestionFormData>({
    defaultValues: {
      number: nextNumber,
      name: '',
      points: ''
    }
  });

  const onSubmit = handleSubmit(({ number, name, points }) => {
    if (name === '' || number === '') {
      return;
    }

    // @ts-ignore sometimes the form input is a string, despite the types
    points = points === '' ? 0 : parseInt(points);

    if (isNaN(points)) {
      return;
    }

    workingSeason.onAddQuestion({
      id: randomUUID(),
      name,
      number,
      maximumPoints: points
    });

    setFocus('name', { shouldSelect: true });
    reset();
    // Calculate next number after adding
    const newNextNumber = calculateNextQuestionNumber(workingSeason.seasonView.questions);
    setValue('number', newNextNumber);
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}>
        <Typography>
          New question:
        </Typography>
        <FormInputText
          name="number"
          label="#"
          control={control}
          rules={{ required: true }}
          sx={{ width: 70 }}
        />
        <FormInputText
          name="name"
          label="Name"
          control={control}
          rules={{ required: true }}
          sx={{ width: 240 }}
        />
        <FormInputText
          name="points"
          label="Points"
          control={control}
          sx={{ width: 100 }}
        />
        <Button type="submit" variant="text" startIcon={<AddIcon/>}>
          Add
        </Button>
      </Stack>
    </form>
  );
}
